import { Cover } from "../display/Cover.js";
import { Header } from "../display/Header.js";
import { Help } from "../display/Help.js";
import { MidiDeviceStateView } from "../display/MidiDeviceStateView.js";
import { Mixer } from "../display/Mixer.js";
import { MixerReset } from "../display/MixerReset.js";
import { Navigation } from "../display/Navigation.js";
import { Orchestra } from "../display/Orchestra.js";
import { OrchestraSelector } from "../display/OrchestraSelector.js";
import { Stage } from "../display/Stage.js";
import { Data } from "../model/Data.js";
import { VOOrchestra } from "../model/DataStructure.js";
import { Idle } from "../model/Idle.js";
import { MidiDevice } from "../model/MidiDevice.js";
import { ProgressList } from "../model/ProgressList.js";

export interface OrchestraAppOptions {
    element: HTMLDivElement;
}

export class OrchestraApp {

    private _element: HTMLDivElement;
    private _stage: Stage;
    private _header: Header;
    private _navigation: Navigation;
    private _orchestras: Orchestra[] = [];
    private _currentOrchestra: Orchestra;
    private _orchestraSelector: OrchestraSelector;
    private _data: Data;
    private _mixer: Mixer;
    private _midi: MidiDevice = null;
    private _help: Help;
    private _mixerReset: MixerReset;
    private _isOpening: boolean = false;
    private _cover: Cover;

    constructor(options: OrchestraAppOptions) {
        options = e5.object.merge(
            {
            },
            options
        );
        this._element = options.element;

        this.init(options);
    }

    private async init(options: OrchestraAppOptions) {
        const isRuntime = Boolean(window.api);
        this._stage = new Stage({
            element: this._element,
            className: "orchestra-app",
            elementOffsetHeight: isRuntime ? 0 : 200
        });

        try {
            const assetsUrl = this._element.getAttribute("data-base") + "/assets";
            const useMidi = isRuntime || e5.Url.params.has("midi");
            let showMixer = !useMidi || e5.Url.params.has("mixer");

            let dataProgress = this._stage.preloader.addProgressList(
                new ProgressList(
                    {
                        label: "Daten"
                    }));

            let audioDownloadProgress = this._stage.preloader.addProgressList(
                new ProgressList(
                    {
                        label: "Audio Download",
                        format: "filesize"
                    }));

            let audioProcessingProgress = this._stage.preloader.addProgressList(
                new ProgressList(
                    {
                        label: "Audio Verarbeitung"
                    }));

            this._data = new Data();

            await this._data.load(this._element.getAttribute("data-data"), dataProgress);

            const orchestras = this._data.config.orchestra_list.orchestras;

            if (useMidi
                && MidiDevice.isSupported) {
                this._midi = new MidiDevice({
                });

                new MidiDeviceStateView({
                    container: this._stage.overlay,
                    midi: this._midi
                });

                let skipWaitingForMidiReady = (e: KeyboardEvent) => {
                    if (e5.KeyCode.is(e, e5.KeyCode.ESCAPE)) {
                        showMixer = true;
                        this._midi.forceReady();
                    }
                };

                document.addEventListener("keyup", skipWaitingForMidiReady);

                await this._midi.onReady();

                document.removeEventListener("keyup", skipWaitingForMidiReady);

            } else if (useMidi) {
                throw new Error("Leider wird die Web MIDI API nicht unterstützt.");
            }

            this._header = new Header({
                container: this._stage.foreground,
                visible: false,
                collapsed: true
            });

            this._help = new Help({
                container: this._stage.overlay,
                info: this._data.config.orchestra_info
            });

            let index = 0;
            for (let orchestra of orchestras) {
                orchestra.color = this._data.colors.fromIndex(index++);
            }

            this._mixer = new Mixer({
                container: showMixer ? this._stage.overlay : null,
                position: "bottom-outer",
                style: "real",
                visible: false,
                midi: this._midi,
                slidable: true,
                faderInitialState: this._data.config.mixer_fader_initial_state
            });

            this._mixerReset = new MixerReset({
                container: this._stage.overlay,
                mixer: this._mixer,
                labels: this._data.labels
            });

            this._navigation = new Navigation({
                container: this._stage.foreground,
                visible: false
            });

            this._navigation.addButton({
                icon: "icon-588",
                title: "Home",
                onClick: async () => {
                    this.openOrchestra(null);
                }
            });

            this._navigation.addButton({
                icon: "icon-589",
                title: "Restart",
                onClick: () => {
                    if (this._currentOrchestra) {
                        this._currentOrchestra.restart();
                    }
                }
            });

            this._navigation.addButton({
                icon: "icon-587",
                title: "Help",
                onClick: () => {
                    this._help.visible = true;
                }
            });

            await Promise.all(orchestras.map(
                (orchestra) => {
                    let o = new Orchestra({
                        container: this._stage.content,
                        data: orchestra,
                        mixer: this._mixer,
                        labels: this._data.labels,
                        instrumentGradient: this._data.config.orchestra_instrument_gradient,
                        instrumentShadow: this._data.config.orchestra_instrument_shadow,
                        showConnections: this._data.config.orchestra_show_lines,
                        showTotalSoundwave: this._data.config.orchestra_sound_control,
                        totalSoundwaveDynamicVolume: this._data.config.orchestra_sound_control_dynamic
                    });
                    this._orchestras.push(o);

                    return o.create(audioDownloadProgress, audioProcessingProgress);
                })
            );

            this._orchestraSelector = new OrchestraSelector({
                container: this._stage.content,
                orchestras: orchestras,
                visible: false,
                labels: this._data.labels
            });
            this._orchestraSelector.onSelect.add((e) => this.openOrchestra(e.orchestra));

            this._header.visible = true;
            await this._orchestraSelector.show();
            this._mixer.visible = true;
            this._mixerReset.showIfMisaligned();

            const idle = new Idle({
                duration: this._data.config.orchestra_idle_duration * 60 * 1000,
                onBusy: () => {
                    this.setBusy();

                },
                onIdle: () => {
                    this.setIdle();
                }
            });
            this._mixer.onChange.add(() => {
                idle.setBusy();
            });


            // console.log(window.performance.memory);
            // this.test();

        } catch (error) {
            var err = error as Error;
            console.error(error);
            this._stage.preloader.showError(err?.message || "Es ist ein Unbekannter Fehler aufgetreten.");
            return;
        }

        this._stage.preloader.hide();
    }

    // private async test(){
    //     if (performance.measureUserAgentSpecificMemory) {
    //         let result;
    //         try {
    //           result = await performance.measureUserAgentSpecificMemory();
    //         } catch (error) {
    //           if (error instanceof DOMException &&
    //               error.name === "SecurityError") {
    //             console.log("The context is not secure.");
    //           } else {
    //             throw error;
    //           }
    //         }
    //         console.log(result);
    //       }
    // }

    private setBusy(): void {
        this._orchestraSelector.isIdle = false;
    }

    private setIdle(): void {
        this._orchestraSelector.isIdle = true;
        this.openOrchestra(null);
        this._mixerReset.showIfMisaligned();
        this._help.visible = false;
    }

    private async openOrchestra(o: VOOrchestra) {
        let orchestra = o ? this._orchestras.find((v) => v.data.id === o.id) : null;
        if (this._currentOrchestra === orchestra) {
            return;
        }

        if (this._isOpening) {
            console.warn("Could not open orchestra during animation");
            return;
        }

        this._isOpening = true;

        // await this._cover.show();

        if (orchestra) {
            this._stage.content.appendChild(orchestra.element);
            this._orchestraSelector.hide();
        } else {
            this._stage.content.appendChild(this._orchestraSelector.element);
            this._orchestraSelector.show();
            this._header.collapsed = true;
        }

        if (this._currentOrchestra) {
            this._navigation.visible = false;
            await this._currentOrchestra.hide();
            // this._currentOrchestra.hide();
        }

        this._currentOrchestra = orchestra;

        if (this._currentOrchestra) {
            // await this._selector.hide();
            this._header.title = this._currentOrchestra.data.name;
            this._header.subtitle = this._currentOrchestra.data.subtitle;
            this._header.collapsed = false;
            this._navigation.visible = true;

            await this._currentOrchestra.show();
            // this._currentOrchestra.show();
        } else {
            this._help.visible = false;
            // await this._selector.show();
        }

        // await this._cover.hide();

        this._isOpening = false;
    }
}